import React from 'react';
import { get, isEmpty, noop, isArray, join, find } from 'lodash';
import classnames from 'classnames';
import ReactTooltip from 'react-tooltip';

// local dependencies
import { getLabel } from 'utils/getLabel';
import { getDisplayValue } from 'resolvers/formatValue';
import { DATA_TYPES } from 'constants/common';
import { Icon } from 'modules/icon-bank';

// cell view based on type
const CellView = ( { item, value, isActionActive, isFieldEditable, isInfoVisible,  tooltipMessage, onActionClick, cellClass, valueColor = undefined } = {} ) => {
    
    // class-name
    const cellClassName = classnames(
        'ui-entity-info-cell-view__value', cellClass,
        `ui-entity-info-cell-view__value--${item.type}`, {
            'ui-entity-info-cell-view__value--actionable': isActionActive && ! isEmpty( item.actionConfig ),
            'ui-entity-info-cell-view__value--editable': isFieldEditable,
            'ui-entity-info-cell-view__value--info-visible': isInfoVisible
        }
    );

    // Adding value to item object
    item.value = value;

    // cell view for address type
    if( DATA_TYPES.ADDRESS === item.type ) {
        return (
            <div
                className={ cellClassName }
                dangerouslySetInnerHTML={ { __html: value } }
            />
        );
    } else if ( isFieldEditable ) {
        return (
            <div className={ cellClassName } >
                <div className={ 'ui-entity-info-cell-view__value__text' }>
                    { value }
                </div>
                <div
                    className={ 'ui-entity-info-cell-view__value__action-icon' }
                    onClick={ () => onActionClick( { type: 'cell', item } ) }
                >
                    <Icon name='edit' width='14' height='14' />
                </div>
            </div>
        );
    } else {
        return (
            <div
                className={ cellClassName } style= { { color: valueColor } }
                onClick={ () => isActionActive ? onActionClick( { type: 'cell', value: item.actionConfig, item } ) : null }
            >
                <div>{ isArray( value ) ? join( value, ', ' ) : value }</div>
                {
                    isInfoVisible ? (
                        <div className='ui-entity-info-cell-view__value__info-icon'>
                            <ReactTooltip
                                id={ `${ item.name }-tooltip` }
                                aria-haspopup='true'
                                textColor="#000"
                                backgroundColor='#cdd7e1'
                                className='ui-entity-info-cell-view__value__info-icon__tooltip'
                                place="bottom"
                            >{ tooltipMessage }</ReactTooltip>

                            <div data-tip  data-for={ `${ item.name }-tooltip` } data-place="bottom">
                                <Icon name='info' width='14' height='14' />
                            </div>
                        </div>
                    ) : null
                }
            </div>
        );
    }
};

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { entityInfoCellView as EntityInfoCellView } from 'ui/views/widgets/entityInfoCellView';
 *
 * // render
 * <EntityInfoCellView propName='propValue'/>
 */
export const entityInfoCellView = ( props ) => {

    // entiity item data
    const item = get( props, 'item' );

    // get the entity data value
    const value = get( props, 'value' );

    // get the display value
    const displayValue = getDisplayValue( {
        type: item.type,
        value,
        options: get( item, 'options', [] ),
    } );

    // class-name
    const entitiyClassname = classnames( props.className,
        'ui-entity-info-cell-view',
        `ui-entity-info-cell-view--${item.type}`,
        `ui-entity-info-cell-view--${get( item, 'name', undefined )}`
    );

    // get action-config
    const isActionActive = get( props, `actionsVisibility.${item.name}`, false );

    // check if field is editable
    const isFieldEditable = get( props, `actionsVisibility.fieldActions.${item.name}`, false ) && get( item, 'actionConfig.editable', false );

    // check if info icon and tooltip is to be shown
    const uiConfig = get( item, 'uiConfig', {} );
    const isInfoVisible = ! isEmpty( uiConfig ) && get( props, `iconsVisibility.${ uiConfig.info.visibility }`, false ) || item.type === DATA_TYPES.TEXT_WITH_INFO;
    
    let tooltipMessage = null;
    if ( isInfoVisible ) {
        tooltipMessage = ! isEmpty( uiConfig ) ? getLabel( get( uiConfig, 'info.message', {} ) ) : getLabel( get( find( props.options, { value } ), 'description', {} ) );
    }

    // get color config
    const valueColor = get( props.entityConfig, `valueColors.${item.name}.${value}`, undefined );

    return (
        <div className={ entitiyClassname }>
            <div className='ui-entity-info-cell-view__title'> { getLabel( item.title ) } </div>
            <CellView
                item={ item }
                value={ displayValue }
                isActionActive={ isActionActive }
                isFieldEditable={ isFieldEditable }
                isInfoVisible={ isInfoVisible }
                tooltipMessage={ tooltipMessage }
                onActionClick={ props.onActionClick }
                valueColor={ valueColor }
                cellClass= { props.cellClassName }
            />
        </div>
    );
};

// set display name
entityInfoCellView.displayName = 'entityInfoCellView';

// set default props
entityInfoCellView.defaultProps = {
    onCellActionClick: noop
};

// prop types
entityInfoCellView.propTypes = {};
